import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  ChangeEventHandler,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { FAIcon, IconStyle } from '../../Icons/Icons';
import Tooltip, { TooltipPreference } from '../../Tooltip/Tooltip';
import {
  DropDownPropsType,
  Data,
  ConditionResult,
  ConditionInlineResult,
  ConditionResultMulti,
} from '../types/propsType';
import { DropDownValidations } from '../types/enums';
import { useClickOutsideDetector } from '../../../hooks';
import { EVENT_KEY } from '../../../types/enums';
import { useTranslation } from '../../Translation/TranslationProvider';
import styles from './DropDownMLP1.module.scss';

const dropdownContainerCls = (isWithinModal: boolean, disabled: boolean): string => {
  let className = styles['dropdown-container'];
  if (isWithinModal) {
    className = styles['dropdown-container-modal'];
  }
  if (disabled) {
    className += ` ${styles['disabled-container']}`;
  }
  return className;
};

const getClassName = (condition: boolean, className: string) => (condition ? className : '');

export const isDisabledItem = (
  item: Data,
  configData: ConditionResult['configData'] | ConditionInlineResult['configData'] | ConditionResultMulti['configData'],
): boolean => !!(configData?.disableItemKey && item[configData?.disableItemKey]);

/**
 * @description DropDown component provides select options in <DropDown></DropDown> with basic styling
 * Props ->
 * className: prop to get custom styling class
 * value: display value of the dropdown
 * dropdownLabel: prop to add field label above text area
 * status: defines status of entry if any i.e. default,valid or invalid
 * value: defines the selected option from options populated
 * disabled: prop to set disabled or enabled selection
 */

const DropDownMLP1: FC<DropDownPropsType> = ({
  placeholder,
  dropdownLabel = '',
  message,
  className,
  labelInfo = '',
  labelInfoPreference = TooltipPreference.BOTTOM_RIGHT,
  disabled = false,
  readOnly = true,
  autoComplete,
  value = '',
  onChange,
  onBlur,
  onMouseOver,
  onFocus,
  onMouseLeave,
  isPhoneNumberMLP1 = false,
  isCountrySelector = false,
  applySearchFilter = false,
  displayFlagIcon = false,
  searchFilterPlaceHolder,
  searchFilterValue,
  onChangeSearchQuery,
  selectedCountry = {},
  data = [],
  configData,
  status,
  name,
  testId = 'dropdown',
  width,
  top,
  isWithinModal = false,
  onClick,
  ariaLabel,
  id = 'nextgen-dropdown-mlp-1',
  imageConfig: _,
}) => {
  const t = useTranslation();
  const { DEFAULT } = DropDownValidations;
  const dropdownRef = useRef() as MutableRefObject<HTMLSpanElement>;
  const inputRef = useRef() as MutableRefObject<HTMLInputElement>;
  const [drawerDisplay, setDrawerDisplay] = useState(false);

  const ariaLabelValue = ariaLabel || t('nextgen.Dropdown.ariaLabel');

  const [placeholderValue, setPlaceholderValue] = useState(placeholder);
  const handleInputClick = (e: ReactKeyboardEvent | ReactMouseEvent) => {
    setDrawerDisplay((prevState) => !prevState);
    if (onClick) {
      onClick(e);
    }
    setPlaceholderValue(selectedCountry?.name as string);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e);
  const handleSearchInputChange: ChangeEventHandler<HTMLInputElement> = (e) => onChangeSearchQuery?.(e);
  const validateOnlyDigitNumber = (e: ReactKeyboardEvent) => {
    if (['e', 'E', '.', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const selectHandler = (item: Data) => {
    if (isDisabledItem(item, configData)) {
      return;
    }
    configData?.onSelect(item);
    setDrawerDisplay(false);
  };

  useClickOutsideDetector(
    dropdownRef,
    () => {
      setDrawerDisplay(false);
    },
    [data, disabled],
  );
  const statusCls = (cls: string) => (status && status !== DEFAULT && !disabled ? styles[`${cls}-${status}`] : '');

  const icon = (
    <FAIcon
      data-testid={`${testId}-icon-text-id`}
      iconName="chevron-down"
      iconStyle={IconStyle.SOLID}
      className={`${styles['dropdown-icon']} ${getClassName(!disabled, styles['dropdown-icon-disable'])} ${
        styles['icon-desktop']
      }`}
    />
  );

  const getListItem = (item: Data) => {
    let listItem = <></>;
    if (isPhoneNumberMLP1) {
      listItem = (
        <span className={styles['flag-icon-container']}>
          {item.flagIcon ? (
            <>
              {displayFlagIcon && item.flagIcon} {item.phoneCode}
            </>
          ) : null}{' '}
          {item[configData?.label as string] as ReactNode} {item.desc ? ` ${item.desc}` : ''}
        </span>
      );
    } else if (isCountrySelector) {
      listItem = (
        <span className={styles['flag-icon-container']}>
          {item.flagIcon ? <>{item.flagIcon}</> : null} {item[configData?.label as string] as ReactNode}
        </span>
      );
    } else {
      listItem = <p>{item[configData?.label as string] as ReactNode}</p>;
    }

    return listItem;
  };

  const countryClick = () => {
    setDrawerDisplay(true);
    setPlaceholderValue(selectedCountry?.name as string);
    inputRef.current.focus();
  };

  const getPlaceholder = () => {
    if (!isCountrySelector) {
      return placeholder;
    } else {
      return !selectedCountry || drawerDisplay ? placeholderValue : '';
    }
  };

  return (
    <span
      className={`${dropdownContainerCls(isWithinModal, disabled)} ${className || ''}`}
      ref={dropdownRef}
      data-testid={`${testId}-ref-test-id`}
    >
      <label className={styles['dropdown-label']} htmlFor={id}>
        <span className={styles['dropdown-label-text']}>{dropdownLabel}</span>
        {labelInfo && (
          <Tooltip message={labelInfo} preference={labelInfoPreference} className={styles['dropdown-tool-tip']} />
        )}
      </label>
      {/* Dropdown Box with Icon */}
      {/* changed `section` to `div` as `section` doesn't comply with `aria-label`  */}
      {/* as `section` is a non interactive element */}
      <div
        id="nextgen-dropdown-mlp-1"
        className={`${styles['dropdown-box']} ${getClassName(
          isPhoneNumberMLP1 || isCountrySelector,
          styles['with-flag'],
        )} ${getClassName(
          drawerDisplay && !disabled && status === DEFAULT,
          styles['dropdown-field-result'],
        )} ${getClassName(drawerDisplay, styles['dropdown-active'])} ${statusCls('dropdown-validated')} `}
        style={width ? { width } : {}}
        onClick={(e) => !isPhoneNumberMLP1 && !isCountrySelector && handleInputClick(e)}
        onBlur={onBlur}
        onMouseOver={onMouseOver}
        onFocus={onFocus}
        onMouseLeave={onMouseLeave}
        onKeyDown={(e) => e.key === EVENT_KEY.ENTER && !isPhoneNumberMLP1 && !isCountrySelector && handleInputClick(e)}
        tabIndex={0}
        role="button"
        aria-label={ariaLabelValue}
        data-testid={`${testId}-section-test-id`}
      >
        {isPhoneNumberMLP1 && (
          <>
            <button className={styles['phone-number-flag']} onClick={handleInputClick}>
              {(displayFlagIcon && (selectedCountry?.flagIcon as ReactNode)) || null} {icon}
            </button>
            <span> {selectedCountry?.desc as string}</span>
          </>
        )}
        {isCountrySelector && (
          <>
            <button className={styles['phone-number-flag']} onClick={handleInputClick}>
              {(selectedCountry?.flagIcon as ReactNode) || null} {icon}
            </button>
            <span
              role="button"
              tabIndex={0}
              aria-label="country-name"
              onClick={countryClick}
              onKeyDown={countryClick}
              data-testid={`${testId}-selected-country-name-testId`}
            >
              {!drawerDisplay && (selectedCountry?.name as string)}
            </span>
          </>
        )}
        <input
          name={`${name}`}
          value={value}
          id={id}
          ref={inputRef}
          placeholder={getPlaceholder()}
          readOnly={readOnly}
          autoComplete={autoComplete}
          onChange={handleInputChange}
          onKeyDown={(e) => isPhoneNumberMLP1 && validateOnlyDigitNumber(e)}
          type={isPhoneNumberMLP1 ? 'number' : 'text'}
          className={`${data.length > 0 ? styles['dropdown-field'] : styles['dropdown-field-disable']} `}
          disabled={disabled}
          data-testid={`${testId}-test-id`}
          onClick={() => isCountrySelector && countryClick()}
        />
        {!isPhoneNumberMLP1 && !isCountrySelector && icon}
      </div>
      {/* dropdown Result Drawer */}
      <section
        className={
          `${styles['base-dropdown-result']} ` +
          `${drawerDisplay && !disabled ? styles['open-dropdown-result'] : ''} 
          ${statusCls('base-dropdown')}`
        }
        style={{
          ...(width ? { width } : {}),
          ...(top ? { top } : {}),
        }}
        data-testid={`${testId}-results-test-id`}
      >
        {applySearchFilter && (
          <div className={styles['search-input-wrapper']}>
            <input
              type="text"
              className={styles['search-input']}
              placeholder={searchFilterPlaceHolder}
              value={searchFilterValue}
              onChange={handleSearchInputChange}
              data-testid={`${testId}-search-input-test-id`}
            />
            <FAIcon iconName="search" iconStyle={IconStyle.SOLID} className={styles['search-icon']} />
          </div>
        )}
        <ul>
          {data.map((item: Data, index) => (
            <li
              role="presentation"
              key={`dropdown${+index}`}
              className={`${isDisabledItem(item, configData) ? styles['disabled'] : ''} ${
                [value, selectedCountry.name].includes(item[configData?.label || '']) ? styles['selected'] : ''
              }`}
              onClick={() => selectHandler(item)}
              onKeyDown={() => selectHandler(item)}
              data-testid={`${testId}-li-element`}
            >
              {getListItem(item)}
            </li>
          ))}
        </ul>
      </section>
      {!disabled && message && (
        <span
          role="note"
          aria-label="message"
          aria-details={message}
          className={`${styles['dropdown-message']} ${statusCls('dropdown-message')} `}
        >
          {message}
        </span>
      )}
    </span>
  );
};
export default DropDownMLP1;
